import { computed, makeObservable, observable } from "mobx";
import { inject, observer } from "mobx-react";
import { type ChangeEvent, Component } from "react";
import { type IntlShape, injectIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import { DropdownField } from "nvent-web/components/form/DropdownField";
import AppStore from "nvent-web/stores/App";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { type DropdownOption } from "nvent-web/types/DropdownOption";
import { createOption } from "nvent-web/utils/create-option";

interface FloorFinishFieldProps {
  name?: string;
  label?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  onChange?: (floorFinish: string) => void;
  initialValue?: string;
}

interface FloorFinishFieldInnerProps extends FloorFinishFieldProps {
  intl: IntlShape;
  app: AppStore;
  rooms: RoomsStore;
}

class FloorFinishFieldInner extends Component<FloorFinishFieldInnerProps> {
  private initialValue?: string;
  private rooms: RoomsStore;
  private app: AppStore;

  constructor(props: FloorFinishFieldInnerProps) {
    super(props);

    this.initialValue = props.initialValue;
    this.rooms = props.rooms;
    this.app = props.app;

    makeObservable<
      FloorFinishFieldInner,
      "floorFinishOptions" | "initialValue" | "rooms"
    >(this, {
      initialValue: observable,
      floorFinishOptions: computed,
      rooms: observable,
    });
  }

  private get floorFinishOptions(): DropdownOption[] {
    const { floorFinishOptions } = this.rooms;
    const { locale } = this.app;

    return floorFinishOptions
      .filter((option) => option.id === this.initialValue || !option.deprecated)
      .map(({ name, id }) => createOption({ id, name }, locale));
  }

  render() {
    const {
      intl,
      name = "floorFinish",
      label,
      placeholder,
      required,
    } = this.props;

    const labelValue = label || <FormattedMessage id="form.floorFinish" />;
    const placeholderValue =
      placeholder ||
      intl.formatMessage({ id: "form.floorFinish.selectPlaceholder" });

    return (
      <DropdownField
        name={name}
        label={labelValue}
        options={this.floorFinishOptions}
        placeholder={placeholderValue}
        required={required}
        onChange={this.handleFloorFinishChange}
      />
    );
  }

  private handleFloorFinishChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(event.target.value);
    }
  };
}

const FloorFinishField = compose<FloorFinishFieldProps>(
  injectIntl,
  inject("app", "rooms"),
  observer
)(FloorFinishFieldInner);

export default FloorFinishField;
